// AppShellDrawerProvider / useAppShellDrawer (RFC-0014 Phase 1 Task 1.2).
//
// Shared open/close state for the mobile app-shell navigation drawer, mirroring
// the shape of context/connect-repo.tsx (createContext + { isOpen, open, close },
// safe no-op default outside a provider, useCallback/useMemo).
//
// This provider lives INSIDE the router (mounted in PortalLayout, Task 1.4) so it
// can subscribe to route changes via useLocation and auto-close the drawer on
// navigation — a route change should never leave a stale drawer open.
//
// Note: exporting both the provider and the hook from one file triggers the same
// accepted `react-refresh/only-export-components` lint *warning* as connect-repo.tsx
// / theme.tsx — lint still passes; this mirrors the repo pattern.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

interface AppShellDrawerContextValue {
  /** Whether the mobile navigation drawer is currently open. */
  isOpen: boolean;
  /** Open the drawer. */
  open: () => void;
  /** Close the drawer. */
  close: () => void;
}

const AppShellDrawerContext = createContext<AppShellDrawerContextValue | null>(null);

export function AppShellDrawerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Route-change auto-close: navigating anywhere collapses the drawer so it never
  // lingers over the newly-rendered page. Keyed on pathname only (NOT isOpen) to
  // avoid an update→re-run loop — closing an already-closed drawer is a no-op.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const value = useMemo<AppShellDrawerContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return (
    <AppShellDrawerContext.Provider value={value}>{children}</AppShellDrawerContext.Provider>
  );
}

/**
 * Access the app-shell drawer controls. Returns safe no-op defaults when called
 * outside a provider (never throws), matching connect-repo.tsx's posture.
 */
export function useAppShellDrawer(): AppShellDrawerContextValue {
  const ctx = useContext(AppShellDrawerContext);
  if (ctx) return ctx;
  return { isOpen: false, open: () => {}, close: () => {} };
}
